/**
 * Shared "session expired" redirect helper for every portal (employee, admin,
 * client). Extracted here so all three axios interceptors behave identically:
 *   1. Clear the portal's localStorage keys.
 *   2. Show a one-time "session expired" toast (deduped via sessionStorage).
 *   3. Bounce to the portal's login page carrying `?next=<original path>` so
 *      the login form can restore the previous URL post-signin.
 *
 * A refresh-token attempt happens BEFORE this helper is called (see axios
 * interceptors) — this is the last-resort "session is truly dead" path.
 */

const PORTALS = {
  employee: {
    tokenKey: 'employee_token',
    userKey: 'employee_user',
    refreshKey: 'employee_refresh_token',
    loginPath: '/employee/login',
    pathPrefix: '/employee/',
  },
  admin: {
    tokenKey: 'admin_token',
    userKey: 'admin_user',
    refreshKey: 'admin_refresh_token',
    loginPath: '/admin/login',
    pathPrefix: '/admin/',
  },
  client: {
    tokenKey: 'client_token',
    userKey: 'client_user',
    refreshKey: 'client_refresh_token',
    loginPath: '/client/login',
    pathPrefix: '/client/',
  },
};

const TOAST_DEDUPE_KEY = '__girgit_session_expired_toast';

function fireExpiryToast() {
  // Fire once per navigation to avoid a stack of duplicate toasts when several
  // in-flight requests all 401 at the same time.
  try {
    if (sessionStorage.getItem(TOAST_DEDUPE_KEY) === '1') return;
    sessionStorage.setItem(TOAST_DEDUPE_KEY, '1');
    // Lazy import so this module stays framework-agnostic.
    import('sonner').then(({ toast }) => {
      toast.error('Session expired. Please sign in again.', { duration: 4000 });
    }).catch(() => {});
  } catch {}
}

/**
 * Clear the portal's local session and bounce to /portal/login?next=…
 * Idempotent — safe to call from multiple failing requests concurrently.
 */
export function sessionRedirect(portal) {
  const cfg = PORTALS[portal];
  if (!cfg) return;
  try {
    localStorage.removeItem(cfg.tokenKey);
    localStorage.removeItem(cfg.userKey);
    localStorage.removeItem(cfg.refreshKey);
  } catch {}

  // Only trigger a redirect once per navigation.
  if (window.__girgit_redirecting === portal) return;

  const path = window.location.pathname;
  // If we're already on the login page for this portal, nothing to do.
  if (path.startsWith(cfg.loginPath)) return;
  // Only bounce out from within the portal — don't hijack unrelated pages.
  if (!path.startsWith(cfg.pathPrefix)) return;

  window.__girgit_redirecting = portal;
  fireExpiryToast();

  const next = encodeURIComponent(path + window.location.search);
  window.location.href = `${cfg.loginPath}?next=${next}`;
}

/**
 * Called by the login page on successful sign-in to clear the dedupe flag so
 * subsequent sessions can toast again if they later expire.
 */
export function clearSessionExpiryFlag() {
  try { sessionStorage.removeItem(TOAST_DEDUPE_KEY); } catch {}
  window.__girgit_redirecting = null;
}

/**
 * Read `?next=` from the current URL (if any) and return a safe relative path,
 * defaulting to the portal's canonical landing page. Login pages call this to
 * pick where to send the user after signin.
 */
export function getNextPathAfterLogin(defaultPath) {
  try {
    const params = new URLSearchParams(window.location.search);
    const next = params.get('next');
    if (!next) return defaultPath;
    // Reject absolute URLs — only allow same-origin relative paths.
    if (!next.startsWith('/') || next.startsWith('//')) return defaultPath;
    return next;
  } catch {
    return defaultPath;
  }
}
